import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import SEOHead from '../components/SEOHead';

const { FiMenu, FiX, FiChevronDown, FiCheck, FiClock, FiPhone, FiMessageCircle } = FiIcons;

const NileshSpecialOfferPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // OFFER EXPIRATION CONTROL - Set to true to show expired overlay
  const [isOfferExpired, setIsOfferExpired] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const scrollToSection = (sectionId) => {
    if (isOfferExpired) return; // Prevent scrolling when expired
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  // BRAND SLIDER UPDATED TO TEXT
  const brands = [
    {
      name: 'FOTOVILLA',
      url: 'https://fotovilla.in'
    },
    {
      name: 'THE IMPRESSIO',
      url: 'https://www.theimpressio.in/'
    },
    {
      name: 'ISKCON VRINDAVAN',
      url: 'https://iskconvrindavan.com/'
    },
    {
      name: 'ISHA LIFE',
      url: 'https://ishalife.com'
    },
    {
      name: 'UNITE CARE',
      url: 'https://info.unite.care'
    },
    {
      name: 'LAKMÉ',
      url: 'https://www.lakmeindia.com/?srsltid=AfmBOopgMePzsw-hCegpjxyrHoAU1985Bj2p3AjsywJhjLpfyTObqr5u'
    },
    {
      name: 'DERMALOGICA',
      url: 'https://www.dermalogica.com/'
    }
  ];

  // Split brands into two rows for better display
  const firstRowBrands = brands.slice(0, 4); // First 4 brands
  const secondRowBrands = brands.slice(4); // Remaining 3 brands

  return (
    <>
      <SEOHead
        title="Offer Expired - Nilesh Ji Special Offer"
        description="This exclusive offer has expired. Contact us to request a new personalized offer for your digital marketing needs."
        canonical="https://lp.itskaivalya.com/nilesh-ji-special-offer"
        ogTitle="Offer Expired - Nilesh Ji Special Offer"
        ogDescription="This exclusive offer has expired. Contact us to request a new personalized offer for your digital marketing needs."
        ogUrl="https://lp.itskaivalya.com/nilesh-ji-special-offer"
      />
      
      <div className="min-h-screen bg-white text-black relative">
        {/* OFFER EXPIRED OVERLAY */}
        {isOfferExpired && (
          <div className="fixed inset-0 bg-white bg-opacity-95 backdrop-blur-sm z-[9999] flex items-center justify-center">
            <div className="max-w-2xl mx-auto px-6 py-12 text-center">
              {/* Expired Icon */}
              <div className="mb-8">
                <div className="w-24 h-24 mx-auto bg-orange-100 rounded-full flex items-center justify-center">
                  <SafeIcon 
                    icon={FiClock} 
                    className="w-12 h-12 text-orange-600" 
                  />
                </div>
              </div>

              {/* Main Message */}
              <h1 
                className="text-3xl md:text-4xl font-bold text-black mb-6"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                This Exclusive Offer Has Expired
              </h1>

              <p 
                className="text-lg md:text-xl text-gray-700 mb-8 leading-relaxed"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Hi Nilesh Ji, your personalized digital marketing offer was time-sensitive and has now expired. But don't worry — we'd love to create a new customized proposal for your sportswear business.
              </p>

              {/* Contact Options */}
              <div className="space-y-4 mb-8">
                <h2 
                  className="text-xl font-semibold text-black mb-4"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  Ready to discuss a new offer?
                </h2>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  {/* WhatsApp Button */}
                  <a
                    href="https://wa.me/917418125909?text=Hi%2C%20my%20previous%20offer%20expired.%20Can%20you%20please%20create%20a%20new%20personalized%20offer%20for%20my%20business%3F"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center bg-green-600 text-white font-semibold py-4 px-8 rounded-lg text-lg hover:bg-green-700 transition-colors duration-200"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    <SafeIcon icon={FiMessageCircle} className="w-5 h-5 mr-3" />
                    WhatsApp Us
                  </a>

                  {/* Call Button */}
                  <a
                    href="tel:+917418125909"
                    className="inline-flex items-center justify-center bg-blue-600 text-white font-semibold py-4 px-8 rounded-lg text-lg hover:bg-blue-700 transition-colors duration-200"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    <SafeIcon icon={FiPhone} className="w-5 h-5 mr-3" />
                    Call Us
                  </a>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-gray-50 rounded-lg p-6 mb-8">
                <h3 
                  className="text-lg font-semibold text-black mb-3"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  Contact Information
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p 
                    className="flex items-center justify-center"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    <SafeIcon icon={FiPhone} className="w-4 h-4 mr-2" />
                    +91 74181 25909
                  </p>
                  <p 
                    className="flex items-center justify-center"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    <SafeIcon icon={FiMessageCircle} className="w-4 h-4 mr-2" />
                    WhatsApp: +91 74181 25909
                  </p>
                </div>
              </div>

              {/* Footer Message */}
              <p 
                className="text-sm text-gray-600 leading-relaxed"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                We understand that timing doesn't always work out perfectly. That's why we're here to create a fresh, customized proposal that fits your current needs and schedule. Let's discuss how we can help grow your sportswear business digitally.
              </p>

              {/* Logo */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-center">
                  <span 
                    className="text-2xl font-bold mr-1"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    K
                  </span>
                  <span 
                    className="text-sm font-medium"
                    style={{ fontFamily: 'Inter, sans-serif', fontVariant: 'small-caps' }}
                  >
                    itskaivalya.com
                  </span>
                </div>
                <p 
                  className="text-xs text-gray-500 mt-2"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  Digital Marketing Solutions That Drive Results
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ORIGINAL LANDING PAGE CONTENT - PRESERVED BUT HIDDEN WHEN EXPIRED */}
        <div className={isOfferExpired ? 'pointer-events-none opacity-30' : ''}>
          {/* START HEADER SECTION (/nilesh-ji-special-offer) */}
          <header 
            className={`fixed top-0 left-0 right-0 z-50 bg-white transition-all duration-300 ${
              isScrolled ? 'shadow-md' : ''
            }`}
          >
            <nav className="flex items-center justify-between px-4 h-14 md:h-16">
              {/* Logo */}
              <div className="flex items-center">
                <span 
                  className="text-2xl md:text-3xl font-bold cursor-pointer"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                  onClick={() => scrollToSection('hero')}
                >
                  K
                </span>
                <span 
                  className="ml-1 text-sm md:text-base font-medium cursor-pointer"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                  onClick={() => scrollToSection('hero')}
                >
                  itskaivalya.com
                </span>
              </div>

              {/* Desktop Navigation - FIXED TO LINK TO PROPER SECTIONS */}
              <div className="hidden md:flex items-center space-x-8">
                {[
                  { label: 'HOME', id: 'hero' },
                  { label: 'SERVICES', id: 'services' },
                  { label: 'PRICING', id: 'pricing' },
                  { label: 'TESTIMONIALS', id: 'testimonials' }
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="text-sm font-medium tracking-wide hover:underline hover:decoration-gray-400 transition-all duration-200"
                    style={{ 
                      fontFamily: 'Inter, sans-serif',
                      letterSpacing: '1px'
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={toggleMobileMenu}
                className="md:hidden p-2 hover:bg-gray-100 rounded transition-colors duration-200"
                aria-label="Toggle mobile menu"
              >
                <SafeIcon 
                  icon={mobileMenuOpen ? FiX : FiMenu} 
                  className="w-6 h-6"
                />
              </button>
            </nav>

            {/* Mobile Menu Drawer - FIXED TO LINK TO PROPER SECTIONS */}
            <div 
              className={`md:hidden fixed top-14 right-0 h-screen w-64 bg-white transform transition-transform duration-300 ease-in-out shadow-lg ${
                mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
              }`}
            >
              <div className="flex flex-col py-6">
                {[
                  { label: 'HOME', id: 'hero' },
                  { label: 'SERVICES', id: 'services' },
                  { label: 'PRICING', id: 'pricing' },
                  { label: 'TESTIMONIALS', id: 'testimonials' }
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="text-left px-6 py-4 text-sm font-medium tracking-wide hover:bg-gray-50 transition-colors duration-200"
                    style={{ 
                      fontFamily: 'Inter, sans-serif',
                      letterSpacing: '1px'
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Mobile Menu Overlay */}
            {mobileMenuOpen && (
              <div 
                className="md:hidden fixed inset-0 bg-black bg-opacity-20 z-[-1]"
                onClick={() => setMobileMenuOpen(false)}
              />
            )}
          </header>
          {/* END HEADER SECTION */}

          {/* START HERO SECTION (/nilesh-ji-special-offer) */}
          <section id="hero" className="min-h-[80vh] flex flex-col items-center justify-center px-4 py-20 bg-white">
            <div className="max-w-4xl mx-auto text-center">
              {/* Headline */}
              <h1 
                className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight mb-6"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Nilesh Ji, grow your sportswear business digitally — from offline shop to online brand.
              </h1>
              
              {/* Subhead */}
              <p 
                className="text-lg sm:text-xl md:text-2xl font-medium text-black leading-relaxed mb-16"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                We don't get paid for posting. We get paid for growing your revenue.
              </p>
              
              {/* Scroll Cue */}
              <div className="flex flex-col items-center">
                <span 
                  className="text-sm text-gray-500 mb-2"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  See Services
                </span>
                <button
                  onClick={() => scrollToSection('services')}
                  className="text-gray-500 hover:text-black transition-colors duration-200"
                  aria-label="Scroll to services section"
                >
                  <motion.div
                    animate={{ y: [0, 8, 0] }}
                    transition={{ 
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <SafeIcon icon={FiChevronDown} className="w-8 h-8" />
                  </motion.div>
                </button>
              </div>
            </div>
          </section>
          {/* END HERO SECTION */}

          {/* START SERVICES SECTION (/nilesh-ji-special-offer) */}
          <section id="services" className="py-16 px-4 bg-white">
            <div className="max-w-4xl mx-auto">
              {/* Section Headline */}
              <h2 
                className="text-3xl md:text-4xl font-bold text-center mb-12"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Here's exactly what we'll do for you.
              </h2>

              {/* Quarter 1 */}
              <div className="mb-12 pb-8 border-b border-gray-200">
                <h3 
                  className="text-xl md:text-2xl font-bold uppercase mb-6"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  Quarter 1 (Months 1–3)
                </h3>
                <ul className="space-y-3">
                  {[
                    "Brand identity: logo, color palette, typography, brand book.",
                    "Website: fast, mobile-first, WhatsApp ordering integration.",
                    "Hosting: Free while services continue.",
                    "Domain: ₹1,000/year (paid separately by client).",
                    "Social media profiles: Facebook Page, Instagram Page, YouTube channel, WhatsApp Catalogue, Website Checkout.",
                    "Local business listings: Google My Business, JustDial, Sulekha.",
                    "Ecommerce listings: Amazon, Flipkart, Meesho, IndiaMART, eBay.",
                    "Ads account setup & verification (Meta + Ecommerce).",
                    "QR-code flyers for offers, testimonials, and WhatsApp list building."
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <SafeIcon 
                        icon={FiCheck} 
                        className="w-5 h-5 text-black mt-0.5 mr-3 flex-shrink-0" 
                      />
                      <span 
                        className="text-base md:text-lg"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Quarter 2 */}
              <div className="mb-12 pb-8 border-b border-gray-200">
                <h3 
                  className="text-xl md:text-2xl font-bold uppercase mb-6"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  Quarter 2 (Months 4–6)
                </h3>
                <ul className="space-y-3">
                  {[
                    "Launch ecommerce native ads.",
                    "Start Meta ads (Facebook + Instagram).",
                    "Incentivized testimonials system: Scan QR → give testimonial → get coupon.",
                    "Collect reviews: Google My Business, video testimonials, ecommerce platform reviews."
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <SafeIcon 
                        icon={FiCheck} 
                        className="w-5 h-5 text-black mt-0.5 mr-3 flex-shrink-0" 
                      />
                      <span 
                        className="text-base md:text-lg"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Quarter 3 & 4 */}
              <div className="mb-12">
                <h3 
                  className="text-xl md:text-2xl font-bold uppercase mb-6"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  Quarter 3 & 4 (Months 7–12)
                </h3>
                <ul className="space-y-3">
                  {[
                    "Scale ad campaigns and optimize.",
                    "Retention funnel: Everyone scanning QR auto-added to WhatsApp messaging system → client can send repeat offers.",
                    "Quarterly reviews and strategy adjustments."
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <SafeIcon 
                        icon={FiCheck} 
                        className="w-5 h-5 text-black mt-0.5 mr-3 flex-shrink-0" 
                      />
                      <span 
                        className="text-base md:text-lg"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
          {/* END SERVICES SECTION */}

          {/* START QUOTATION-STYLE PRICING SECTION (/nilesh-ji-special-offer) */}
          {/* AD BUDGET REMOVED - NO ADVERTISING BUDGET MENTIONS */}
          <section id="pricing" className="py-16 px-4 bg-white nilesh-quote">
            <div className="max-w-5xl mx-auto">
              {/* Section Headline */}
              <h2 
                className="text-3xl md:text-4xl font-bold text-center mb-12"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Quotation — Digital Marketing Services for Nilesh Pardeshi Ji
              </h2>

              {/* Quotation Table */}
              <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden mb-6 shadow-sm">
                {/* Desktop Table */}
                <div className="hidden md:block">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th 
                          className="px-6 py-4 text-left text-sm font-bold text-black uppercase tracking-wider"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          Deliverable / Item
                        </th>
                        <th 
                          className="px-6 py-4 text-left text-sm font-bold text-black uppercase tracking-wider"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          Description
                        </th>
                        <th 
                          className="px-6 py-4 text-right text-sm font-bold text-black uppercase tracking-wider"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          Charges (₹)
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr>
                        <td 
                          className="px-6 py-4 text-sm font-medium text-black"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          Service Fee (Monthly)
                        </td>
                        <td 
                          className="px-6 py-4 text-sm text-black"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          Professional digital growth services.
                        </td>
                        <td 
                          className="px-6 py-4 text-sm font-medium text-black text-right"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          10,000 / month
                        </td>
                      </tr>
                      <tr>
                        <td 
                          className="px-6 py-4 text-sm font-medium text-black"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          Billing Cycle
                        </td>
                        <td 
                          className="px-6 py-4 text-sm text-black"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          Paid quarterly in advance.
                        </td>
                        <td 
                          className="px-6 py-4 text-sm font-medium text-black text-right"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          30,000 (first advance)
                        </td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td 
                          className="px-6 py-4 text-sm font-bold text-black"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          First Quarter Advance
                        </td>
                        <td 
                          className="px-6 py-4 text-sm text-black"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          This is the amount due today to start work (covers Months 1–3).
                        </td>
                        <td 
                          className="px-6 py-4 text-sm font-bold text-black text-right"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          30,000
                        </td>
                      </tr>
                      <tr>
                        <td 
                          className="px-6 py-4 text-sm font-medium text-black"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          Domain
                        </td>
                        <td 
                          className="px-6 py-4 text-sm text-black"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          New domain purchase (client-owned).
                        </td>
                        <td 
                          className="px-6 py-4 text-sm font-medium text-black text-right"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          1,000 / year
                        </td>
                      </tr>
                      <tr>
                        <td 
                          className="px-6 py-4 text-sm font-medium text-black"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          Website Hosting
                        </td>
                        <td 
                          className="px-6 py-4 text-sm text-black"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          Hosting included free as long as our services continue.
                        </td>
                        <td 
                          className="px-6 py-4 text-sm font-medium text-black text-right"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          0
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Mobile Stacked Layout */}
                <div className="md:hidden">
                  {/* Header */}
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <h3 
                      className="text-sm font-bold text-black uppercase tracking-wider text-center"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      Service Quotation
                    </h3>
                  </div>

                  {/* Mobile Items */}
                  <div className="divide-y divide-gray-200">
                    {[
                      {
                        item: "Service Fee (Monthly)",
                        description: "Professional digital growth services.",
                        charge: "10,000 / month",
                        highlight: false
                      },
                      {
                        item: "Billing Cycle",
                        description: "Paid quarterly in advance.",
                        charge: "30,000 (first advance)",
                        highlight: false
                      },
                      {
                        item: "First Quarter Advance",
                        description: "This is the amount due today to start work (covers Months 1–3).",
                        charge: "30,000",
                        highlight: true
                      },
                      {
                        item: "Domain",
                        description: "New domain purchase (client-owned).",
                        charge: "1,000 / year",
                        highlight: false
                      },
                      {
                        item: "Website Hosting",
                        description: "Hosting included free as long as our services continue.",
                        charge: "0",
                        highlight: false
                      }
                    ].map((row, index) => (
                      <div key={index} className={`px-4 py-4 ${row.highlight ? 'bg-gray-50' : ''}`}>
                        <div className="mb-2">
                          <span 
                            className={`text-sm ${row.highlight ? 'font-bold' : 'font-medium'} text-black uppercase tracking-wide`}
                            style={{ fontFamily: 'Inter, sans-serif' }}
                          >
                            {row.item}
                          </span>
                        </div>
                        <div className="mb-2">
                          <span 
                            className="text-sm text-black"
                            style={{ fontFamily: 'Inter, sans-serif' }}
                          >
                            {row.description}
                          </span>
                        </div>
                        <div>
                          <span 
                            className={`text-sm ${row.highlight ? 'font-bold' : 'font-medium'} text-black`}
                            style={{ fontFamily: 'Inter, sans-serif' }}
                          >
                            ₹{row.charge}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* First Advance Summary */}
              <div className="mb-8 text-center">
                <p 
                  className="text-xl md:text-2xl font-medium text-black"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  First advance due today: <strong>₹30,000</strong> (this starts the work for the first 3 months).
                </p>
              </div>

              {/* Guarantee Statement */}
              <div className="mb-8">
                <div className="border-2 border-gray-300 rounded-lg p-6 bg-gray-50">
                  <p 
                    className="text-lg md:text-xl font-medium text-black text-center leading-relaxed"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    100% Satisfaction Guarantee. We go above and beyond to ensure revenue growth. If we do not deliver measurable improvements, we will work with you until we do.
                  </p>
                </div>
              </div>

              {/* CTA Button */}
              <div className="max-w-md mx-auto">
                <a
                  href="https://wa.me/917418125909?text=Hi%2C%20I%20want%20to%20get%20started.%20Please%20share%20your%20UPI%20QR%20code%20so%20I%20can%20make%20the%20payment."
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get started on WhatsApp"
                  className="block w-full bg-black text-white font-bold py-4 px-8 rounded-lg text-lg md:text-xl hover:bg-gray-800 transition-colors duration-200 text-center"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  I agree — let's get started
                </a>
              </div>
            </div>

            {/* Scoped CSS for this section only */}
            <style jsx>{`
              .nilesh-quote .bg-gray-50 {
                background-color: #F7F7F7;
              }
              
              .nilesh-quote table tr:hover {
                background-color: rgba(0, 0, 0, 0.02);
              }
              
              .nilesh-quote a:focus {
                outline: 2px solid #6B7280;
                outline-offset: 2px;
              }

              @media (max-width: 768px) {
                .nilesh-quote .divide-y > div:last-child {
                  border-bottom: none;
                }
              }
            `}</style>
          </section>
          {/* END QUOTATION-STYLE PRICING SECTION */}

          {/* START TESTIMONIALS SECTION (/nilesh-ji-special-offer) */}
          <section id="testimonials" className="py-16 px-4 bg-white">
            <div className="max-w-4xl mx-auto">
              {/* Section Headline */}
              <h2 
                className="text-3xl md:text-4xl font-bold text-center mb-16"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Real Clients. Real Results.
              </h2>

              {/* Text Testimonials */}
              <div className="space-y-12 mb-16">
                {/* Testimonial 1 - Akshay Gavhane */}
                <div className="grid md:grid-cols-3 gap-6 items-center">
                  <div className="md:col-span-1 flex justify-center">
                    <img 
                      src="https://v0-portfoliowebsite-mq.vercel.app/_next/image?url=https%3A%2F%2Fucarecdn.com%2F02194466-b6f1-43fc-817b-6dc80d980fa9%2Fakshaygavhanephoto.jpg&w=1920&q=75"
                      alt="Akshay Gavhane, Fotovilla"
                      className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover shadow-lg"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <blockquote className="mb-4">
                      <p 
                        className="text-lg md:text-xl italic leading-relaxed"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        "I am just impressed by their service quality and working strategy. We are working with his team for website development and marketing. They understood everything very easily and also recommended some better alternatives whenever possible."
                      </p>
                    </blockquote>
                    <cite 
                      className="text-base md:text-lg font-bold not-italic"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      — Akshay Gavhane, Fotovilla
                    </cite>
                  </div>
                </div>

                {/* Divider */}
                <hr className="border-gray-200" />

                {/* Testimonial 2 - Fred Ferguson */}
                <div className="grid md:grid-cols-3 gap-6 items-center">
                  <div className="md:col-span-1 flex justify-center">
                    <img 
                      src="https://v0-portfoliowebsite-mq.vercel.app/_next/image?url=https%3A%2F%2Fucarecdn.com%2Fab329a42-29da-420c-8208-6e19352fc396%2Ffredferguson.jpg&w=1920&q=75"
                      alt="Fred Ferguson, Freelancer"
                      className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover shadow-lg"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <blockquote className="mb-4">
                      <p 
                        className="text-lg md:text-xl italic leading-relaxed"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        "It was my desire to learn all about traffic generation in this crisis time. So that I can get more people to my website and make more sales. This addressed that very well. In fact I learned strategies and methods I have never thought of before."
                      </p>
                    </blockquote>
                    <cite 
                      className="text-base md:text-lg font-bold not-italic"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      — Fred Ferguson, Freelancer
                    </cite>
                  </div>
                </div>

                {/* Divider */}
                <hr className="border-gray-200" />

                {/* Testimonial 3 - Samruddhi and Nihal */}
                <div className="grid md:grid-cols-3 gap-6 items-center">
                  <div className="md:col-span-1 flex justify-center">
                    <img 
                      src="https://v0-portfoliowebsite-mq.vercel.app/_next/image?url=https%3A%2F%2Fucarecdn.com%2Fd76d8bc0-f381-460f-b163-fceb41e5ea41%2Fnihalandsamruddhi.png&w=1920&q=75"
                      alt="Samruddhi and Nihal, Sam's Nutri Hub"
                      className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover shadow-lg"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <blockquote className="mb-4">
                      <p 
                        className="text-lg md:text-xl italic leading-relaxed"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        "Our old marketing wasn't cutting it. Maitreya Labs built our website, fixed our Google ranking, and ran ads that worked. Now, new customers find us online and keep coming back!"
                      </p>
                    </blockquote>
                    <cite 
                      className="text-base md:text-lg font-bold not-italic"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      — Samruddhi and Nihal, Sam's Nutri Hub
                    </cite>
                  </div>
                </div>

                {/* Divider */}
                <hr className="border-gray-200" />

                {/* Testimonial 4 - Akshay Tandale */}
                <div className="grid md:grid-cols3 gap-6 items-center">
                  <div className="md:col-span-1 flex justify-center">
                    <img 
                      src="https://v0-portfoliowebsite-mq.vercel.app/_next/image?url=https%3A%2F%2Fucarecdn.com%2Fc85cee71-c73c-48dd-a01a-a1c4af834f35%2Fakshaytandale.jpg&w=1920&q=75"
                      alt="Akshay Tandale, Bhargav Properties"
                      className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover shadow-lg"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <blockquote className="mb-4">
                      <p 
                        className="text-lg md:text-xl italic leading-relaxed"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        "Kaivalya, thank you man. You are more than this. I can't put the value you provided in words."
                      </p>
                    </blockquote>
                    <cite 
                      className="text-base md:text-lg font-bold not-italic"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      — Akshay Tandale, Bhargav Properties
                    </cite>
                  </div>
                </div>
              </div>

              {/* Video Testimonial */}
              <div className="max-w-3xl mx-auto">
                <div className="mb-6 text-center">
                  <p 
                    className="text-lg md:text-xl font-medium"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    Watch how our work created real change.
                  </p>
                </div>
                
                <div className="wistia_responsive_padding" style={{padding:'177.78% 0 0 0', position:'relative'}}>
                  <div className="wistia_responsive_wrapper" style={{height:'100%', left:0, position:'absolute', top:0, width:'100%'}}>
                    <iframe 
                      src="https://fast.wistia.net/embed/iframe/pjhz34kw16?web_component=true&seo=true"
                      title="Client testimonial video showing real results from our digital marketing work"
                      allow="autoplay; fullscreen"
                      allowTransparency="true"
                      frameBorder="0"
                      scrolling="no"
                      className="wistia_embed rounded-lg shadow-lg"
                      name="wistia_embed"
                      width="100%"
                      height="100%">
                    </iframe>
                  </div>
                </div>
              </div>
            </div>
          </section>
          {/* END TESTIMONIALS SECTION */}

          {/* BRAND SLIDER - TWO ROW LAYOUT WITH DOUBLED TIMING */}
          <section id="brands" className="py-16 px-4 bg-black overflow-hidden">
            <div className="max-w-6xl mx-auto">
              {/* Section Headline */}
              <h2 
                className="text-2xl md:text-3xl font-bold text-center mb-12 text-white"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Trusted by respected brands
              </h2>

              {/* TWO-ROW BRAND SLIDER LAYOUT */}
              <div className="space-y-8">
                {/* First Row - 4 brands */}
                <div className="brands-slider-wrapper">
                  <div className="brands-slider-container">
                    {/* First set of first row brands */}
                    {firstRowBrands.map((brand, index) => (
                      <div key={`row1-set1-${index}`} className="brand-slide">
                        <a
                          href={brand.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="brand-name-link"
                        >
                          {brand.name}
                        </a>
                      </div>
                    ))}
                    {/* Duplicate set for seamless loop */}
                    {firstRowBrands.map((brand, index) => (
                      <div key={`row1-set2-${index}`} className="brand-slide">
                        <a
                          href={brand.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="brand-name-link"
                        >
                          {brand.name}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Second Row - 3 brands (moving in opposite direction) */}
                <div className="brands-slider-wrapper">
                  <div className="brands-slider-container reverse">
                    {/* First set of second row brands */}
                    {secondRowBrands.map((brand, index) => (
                      <div key={`row2-set1-${index}`} className="brand-slide">
                        <a
                          href={brand.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="brand-name-link"
                        >
                          {brand.name}
                        </a>
                      </div>
                    ))}
                    {/* Duplicate set for seamless loop */}
                    {secondRowBrands.map((brand, index) => (
                      <div key={`row2-set2-${index}`} className="brand-slide">
                        <a
                          href={brand.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="brand-name-link"
                        >
                          {brand.name}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* UPDATED CSS WITH DOUBLED TIMING AND TWO-ROW LAYOUT */}
            <style jsx>{`
              .brands-slider-wrapper {
                position: relative;
                width: 100%;
                overflow: hidden;
                mask-image: linear-gradient(
                  to right,
                  transparent 0%,
                  black 10%,
                  black 90%,
                  transparent 100%
                );
              }

              .brands-slider-container {
                display: flex;
                width: calc(200% + 160px);
                animation: slideLeft 24s linear infinite;
              }

              .brands-slider-container.reverse {
                animation: slideRight 20s linear infinite;
              }

              .brand-slide {
                flex: 0 0 auto;
                min-width: 280px;
                padding: 0 40px;
                display: flex;
                align-items: center;
                justify-content: center;
              }

              .brand-name-link {
                display: block;
                padding: 20px 0;
                font-family: 'Inter', sans-serif;
                font-size: 1.25rem;
                font-weight: 700;
                color: white;
                text-decoration: none;
                white-space: nowrap;
                text-align: center;
                transition: all 0.3s ease;
              }

              .brand-name-link:hover {
                color: #d1d5db;
                transform: scale(1.05);
              }

              @keyframes slideLeft {
                0% {
                  transform: translateX(0);
                }
                100% {
                  transform: translateX(-50%);
                }
              }

              @keyframes slideRight {
                0% {
                  transform: translateX(-50%);
                }
                100% {
                  transform: translateX(0);
                }
              }

              .brands-slider-container:hover {
                animation-play-state: paused;
              }

              /* Mobile optimizations */
              @media (max-width: 640px) {
                .brands-slider-container {
                  animation-duration: 16s;
                }
                
                .brands-slider-container.reverse {
                  animation-duration: 14s;
                }
                
                .brand-slide {
                  min-width: 220px;
                  padding: 0 30px;
                }
                
                .brand-name-link {
                  font-size: 1rem;
                  padding: 16px 0;
                }
              }

              /* Desktop optimizations */
              @media (min-width: 1024px) {
                .brands-slider-container {
                  animation-duration: 30s;
                }
                
                .brands-slider-container.reverse {
                  animation-duration: 25s;
                }
                
                .brand-slide {
                  min-width: 320px;
                  padding: 0 50px;
                }
                
                .brand-name-link {
                  font-size: 1.5rem;
                  padding: 24px 0;
                }
              }
            `}</style>
          </section>
          {/* END BRAND LOGOS SECTION */}

          {/* START ABOUT KAIVALYA SECTION (/nilesh-ji-special-offer) */}
          <section className="py-16 px-6">
            <div className="max-w-4xl mx-auto">
              <h2 
                className="text-3xl md:text-4xl font-bold text-black mb-12 text-center"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Meet Your Partner: Kaivalya
              </h2>
              
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <img 
                    src="https://i.ibb.co/DHSRjPSv/kaivalya-profile-photo.jpg" 
                    alt="Kaivalya - Digital Marketing Expert"
                    className="w-full max-w-md mx-auto rounded-lg shadow-lg"
                  />
                </div>
                <div className="space-y-6">
                  <p 
                    className="text-lg text-gray-700"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    I've built and deployed dozens of digital marketing systems and applications, from e-commerce platforms handling thousands of customers to business tools generating consistent revenue streams.
                  </p>
                  <p 
                    className="text-lg text-gray-700"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    But more importantly, I understand what it's like to run a local business: the pressure of competition, the challenge of reaching new customers, and the need for results that directly impact your bottom line.
                  </p>
                  <p 
                    className="text-lg text-gray-700"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    That's why I created this service. Not to impress you with technical jargon, but to give you the exact digital systems that turn browsers into buyers and one-time customers into loyal advocates.
                  </p>
                  <p 
                    className="text-lg font-semibold text-black"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    My commitment: I'll work alongside you every step of the way, ensuring every digital touchpoint drives real business growth for your sportswear shop.
                  </p>
                  
                  <div className="grid grid-cols-3 gap-4 mt-8">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-black">53+</p>
                      <p className="text-sm text-gray-600">Digital Businesses Built</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-black">120+</p>
                      <p className="text-sm text-gray-600">Local Businesses Helped</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-black">4.2</p>
                      <p className="text-sm text-gray-600">Years Specializing in Digital Growth</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          {/* END ABOUT KAIVALYA SECTION */}

          {/* START MAITREYA LABS TEAM SECTION (/nilesh-ji-special-offer) */}
          <section className="py-16 px-6 bg-gray-50">
            <div className="max-w-4xl mx-auto">
              <h2 
                className="text-3xl md:text-4xl font-bold text-black mb-12 text-center"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Meet the Maitreya Labs Team
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8 mb-8">
                <img 
                  src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/maitreya%20labs%20team%2001.jpg-CYzk0EHjNYGm0lsvWJXs0UcNAluuHS.jpeg" 
                  alt="Maitreya Labs Team Member 1"
                  className="w-full rounded-lg shadow-lg"
                />
                <img 
                  src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/maitreya%20labs%20charty%20and%20ngo%20sessions.jpg-gOY6rfvY4MYCc5Wwcxq8Q4sS30vBBv.jpeg" 
                  alt="Maitreya Labs Charity and NGO Sessions"
                  className="w-full rounded-lg shadow-lg"
                />
                <img 
                  src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Team%20Maitreya%20Labs%20team%202%20%281%29.png-nLZY55sOGLxWPnzzzhLt4Xo4NzhgH0.jpeg" 
                  alt="Maitreya Labs Team Member 2"
                  className="w-full rounded-lg shadow-lg"
                />
              </div>
              
              <p 
                className="text-lg text-gray-700 text-center"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                From technical development to community outreach, we're committed to building not just better businesses, but a stronger local economy that benefits everyone in the community.
              </p>
            </div>
          </section>
          {/* END MAITREYA LABS TEAM SECTION */}

          {/* FOOTER - HORIZONTAL MENU FIXED */}
          <footer id="page-footer" className="nilesh-offer-footer bg-white border-t border-gray-200">
            <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
                {/* Left Column - Logo & Copyright */}
                <div className="text-center md:text-left">
                  {/* Logo Line */}
                  <div className="mb-4">
                    <span 
                      className="text-2xl font-bold mr-1"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      K
                    </span>
                    <span 
                      className="text-sm font-medium"
                      style={{ fontFamily: 'Inter, sans-serif', fontVariant: 'small-caps' }}
                    >
                      itskaivalya.com
                    </span>
                  </div>
                  
                  {/* Copyright Line */}
                  <p 
                    className="text-sm text-black leading-relaxed"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    © 2025 Maitreya Labs. We help you fix what matters first.
                  </p>
                </div>

                {/* Right Column - Policy Links (HORIZONTAL LAYOUT) */}
                <div className="text-center md:text-right">
                  <nav>
                    {/* Desktop: Horizontal layout with separators */}
                    <div className="hidden md:flex flex-wrap justify-end items-center gap-1">
                      <a
                        href="https://itskaivalya.com/privacy-policy"
                        className="text-sm text-black hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-colors duration-200"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        Privacy Policy
                      </a>
                      <span className="text-sm text-gray-400 mx-2">|</span>
                      <a
                        href="https://itskaivalya.com/terms-and-conditions"
                        className="text-sm text-black hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 focus: ring-offset-2 transition-colors duration-200"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        Terms and Conditions
                      </a>
                      <span className="text-sm text-gray-400 mx-2">|</span>
                      <a
                        href="https://itskaivalya.com/refund-policy"
                        className="text-sm text-black hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-colors duration-200"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        Refund Policy
                      </a>
                      <span className="text-sm text-gray-400 mx-2">|</span>
                      <a
                        href="https://itskaivalya.com/disclaimer"
                        className="text-sm text-black hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-colors duration-200"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        Disclaimer
                      </a>
                      <span className="text-sm text-gray-400 mx-2">|</span>
                      <a
                        href="https://itskaivalya.com/cookie-policy"
                        className="text-sm text-black hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-colors duration-200"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        Cookie Policy
                      </a>
                    </div>

                    {/* Mobile: Compact horizontal layout */}
                    <div className="md:hidden flex flex-wrap justify-center items-center gap-1 text-xs">
                      <a
                        href="https://itskaivalya.com/privacy-policy"
                        className="text-black hover:text-gray-600 transition-colors duration-200"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        Privacy
                      </a>
                      <span className="text-gray-400">|</span>
                      <a
                        href="https://itskaivalya.com/terms-and-conditions"
                        className="text-black hover:text-gray-600 transition-colors duration-200"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        Terms
                      </a>
                      <span className="text-gray-400">|</span>
                      <a
                        href="https://itskaivalya.com/refund-policy"
                        className="text-black hover:text-gray-600 transition-colors duration-200"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        Refund
                      </a>
                      <span className="text-gray-400">|</span>
                      <a
                        href="https://itskaivalya.com/disclaimer"
                        className="text-black hover:text-gray-600 transition-colors duration-200"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        Disclaimer
                      </a>
                      <span className="text-gray-400">|</span>
                      <a
                        href="https://itskaivalya.com/cookie-policy"
                        className="text-black hover:text-gray-600 transition-colors duration-200"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        Cookies
                      </a>
                    </div>
                  </nav>
                </div>
              </div>
            </div>

            {/* Scoped CSS for this footer only */}
            <style jsx>{`
              .nilesh-offer-footer a:focus {
                outline: 2px solid #6B7280;
                outline-offset: 2px;
              }
            `}</style>
          </footer>
          {/* END FOOTER SECTION */}
        </div>

        {/* Wistia Script */}
        <script src="https://fast.wistia.net/player.js" async></script>
      </div>
    </>
  );
};

export default NileshSpecialOfferPage;